import type { SelectOption } from 'naive-ui';

/** 同时匹配 label 与可选的 keywords（如应用的 id/包名/商店名），keywords 不存在时退化为仅匹配 label */
export function matchSelectOption(option: SelectOption, pattern: string): boolean {
  const kw = pattern.toLowerCase();
  const label = String(option.label ?? '').toLowerCase();
  const value = String(option.value ?? '').toLowerCase();
  const keywords = String((option as { keywords?: unknown }).keywords ?? '').toLowerCase();
  return label.includes(kw) || value.includes(kw) || keywords.includes(kw);
}

export function sortOptions(options: SelectOption[]) {
  return [...options].sort((a, b) => String(a.label ?? '').localeCompare(String(b.label ?? '')));
}

function tokenizeVersion(value: string) {
  return value
    .split(/([0-9]+)/)
    .filter(Boolean)
    .map(item => (/^\d+$/.test(item) ? Number(item) : item.toLowerCase()));
}

export function compareVersionDesc(versionA: string, versionB: string) {
  const tokensA = tokenizeVersion(versionA);
  const tokensB = tokenizeVersion(versionB);
  const maxLength = Math.max(tokensA.length, tokensB.length);

  for (let index = 0; index < maxLength; index += 1) {
    const tokenA = tokensA[index];
    const tokenB = tokensB[index];
    if (tokenA === undefined) return 1;
    if (tokenB === undefined) return -1;
    if (tokenA === tokenB) continue;

    if (typeof tokenA === 'number' && typeof tokenB === 'number') {
      return tokenB - tokenA;
    }

    return String(tokenB).localeCompare(String(tokenA), undefined, { numeric: true });
  }

  return versionB.localeCompare(versionA, undefined, { numeric: true });
}

export function sortVersionOptions(options: SelectOption[]) {
  return [...options].sort((a, b) => compareVersionDesc(String(a.value ?? a.label ?? ''), String(b.value ?? b.label ?? '')));
}

export function toAppOptions(data: Api.Application.Application[] = []) {
  return sortOptions(
    data.map((item, index) => {
      const record = item as Record<string, unknown>;
      const displayName = [item.appName, record.label, item.packageName, item.storeName, item.id]
        .find(value => typeof value === 'string' && value.trim())
        ?.toString() ?? String(item.id);
      const appIdentifiers = [
        item.id,
        record.appId,
        record.appid,
        record.appID,
        record.app_id,
        record.applicationId,
        item.storeId,
        record.store_id
      ];

      return {
        label: displayName,
        value: item.id.toString(),
        keywords: [...appIdentifiers, displayName, item.packageName, item.storeName]
          .filter(value => value !== undefined && value !== null && value !== '')
          .join(' '),
        index
      };
    })
  );
}

export function toCountryOptions(
  data: Api.Statistic.CountryName[] = [],
  locale: App.I18n.LangType = 'zh-CN'
) {
  return sortOptions(
    data.map((item, index) => {
      const countryName = locale === 'en-US' ? item.en : item.chn;

      return {
        label: `${countryName} ${item.abbr}`,
        value: item.abbr,
        keywords: `${item.chn} ${item.en} ${item.abbr}`,
        index
      };
    })
  );
}

export function toStringOptions(data: string[] = [], includeAll = false) {
  const options = data.map((item, index) => ({
    label: item,
    value: item,
    index
  }));

  return includeAll ? [{ label: '全部', value: '', index: -1 }, ...sortOptions(options)] : sortOptions(options);
}

export function toVersionOptions(data: string[] = [], includeAll = false) {
  const options = data.map((item, index) => ({
    label: item,
    value: item,
    index
  }));

  return includeAll ? [{ label: '全部', value: '', index: -1 }, ...sortVersionOptions(options)] : sortVersionOptions(options);
}

export function formatDateParam(timestamp?: number) {
  if (!timestamp) return undefined;
  return new Date(timestamp * 1000).toISOString().slice(0, 10);
}
